import React, { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { breakLiveElement, createLiveElement, invalidateLiveElement } from 'actions/live_elements';
import { useSelectedWidgetSelector } from 'components/Editor/hooks/useEditorSession';

import shortid from 'shortid';
import _ from 'lodash';

import { getWidgetBoundingRectForBreakpoint } from 'components/Editor/helper/layoutManager';
import { LIVE_ELEMENT_CONTAINER } from 'constants/widgetTypes';

import { Button } from 'react-bootstrap';
import { Property } from '../../components/properties/';
import { EditorContext } from "components/Editor";

import styles from './liveElements.module.scss';
import { calculateRequestForBreakpoint } from 'components/Editor/helper/liveElement';
import StoryboardContext from 'components/Storyboard/context';



export const LiveElementCreationProperty = ({ elements, onCancel }) => {
  const dispatch = useDispatch();

  const editorContext = useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const moveableManager = editorContext.editorSession.moveableManager.current;
  const selectedWidgets = useSelectedWidgetSelector(editorSession);

  const targetSelector = _.size(elements) === 1 ? `#${elements[0].uid}` : '[data-able-grouptoolbarviewable="true"]';

  const creating = useSelector(state => _.get(state, 'liveElements.liveElement.create.creating'));
  const created = useSelector(state => _.get(state, 'liveElements.liveElement.create.created'));
  const error = useSelector(state => _.get(state, 'liveElements.liveElement.create.error'));

  const storyboardContext = React.useContext(StoryboardContext);

  useEffect(() => {
    if (created) {
      dispatch(invalidateLiveElement());
    }
  }, [created]);

  const handleMakeLiveElement = () => {
    const parent = _.get(selectedWidgets, '[0].parent', null);
    const name = _.get(selectedWidgets, '[0].name', null);
    var uid = `widget_${shortid.generate()}`

    var widgets = selectedWidgets.filter(widget => widget.parent === parent);
    const desktopRequest = calculateRequestForBreakpoint(editorSession, widgets, 'desktop');
    widgets = desktopRequest.widgets;

    const mobileRequest = calculateRequestForBreakpoint(editorSession, widgets, 'mobile');
    widgets = mobileRequest.widgets;

    var liveElementContainer = {
      uid: uid,
      name: `Live Element Container`,
      parent: null,
      type: LIVE_ELEMENT_CONTAINER,
      properties: {
        desktop: {
          visible: true,
          layout: {
            translate: '0px, 0px',
            width: `${desktopRequest.width}px`,
            height: `${desktopRequest.height}px`
          }
        },
        mobile: {
          visible: true,
          layout: {
            translate: '0px, 0px',
            width: `${mobileRequest.width}px`,
            height: `${mobileRequest.height}px`
          }
        }
      }
    }

    var request = {
      name: "Live Element",
      parent: parent,
      layout: {
        desktop: {
          x: desktopRequest.x,
          y: desktopRequest.y,
          width: desktopRequest.width,
          height: desktopRequest.height
        },
        mobile: {
          x: mobileRequest.x,
          y: mobileRequest.y,
          width: mobileRequest.width,
          height: mobileRequest.height
        }
      },
      container: liveElementContainer,
      widgets: widgets,
      themes: _.get(storyboardContext, 'themes'),
    }

    dispatch(createLiveElement(editorSession, request));
  }

  const handleClose = () => {
    if (onCancel) {
      onCancel();
    }
  }

  return (
    <Property.Dialog className={styles.styleDialog}
      width={233}
      target={targetSelector}
      title={'Live Element:'}
      onCancel={handleClose}
    >
      <Property.Body>
        <div className={styles.container}>
          <p>A Live Element is an element that you can reuse in a storyboard. </p>
          <p>Live elements can be accessed/managed from the widget panel. </p>

          {error &&
            <p className={styles.error}>
              {_.get(error, 'errors.0.message', 'Unable to create live element')}
            </p>
          }

          <p>Are you sure you want to continue?</p>
          <div className={styles.actions}>
            <Button
              size='sm'
              className={styles.action}
              variant='secondary'
              disabled={creating}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMakeLiveElement}
              size='sm'
              className={styles.action}
              variant='primary'
              disabled={creating}
            >
              {creating ? `Creating...` : `Confirm`}
            </Button>
          </div>
        </div>
      </Property.Body>
    </Property.Dialog>
  )
}

export const BreakLiveElementDialog = ({ widget, onCancel }) => {
  const dispatch = useDispatch();

  const editorContext = useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const id = _.get(widget, 'uid');

  const handleBreakLiveElement = () => {
    dispatch(breakLiveElement(editorSession, widget));
  }

  const handleClose = () => {
    if (onCancel) {
      onCancel();
    }
  }

  return (
    <Property.Dialog className={styles.styleDialog}
      width={233}
      target={`#${id}`}
      title={'Break the link:'}
      onCancel={handleClose}
    >
      <Property.Body>
        <div className={styles.container}>
          <p>Breaking the link will make the live element a set of normal elements</p>
          <p>Are you sure you want to continue?</p>
          <div className={styles.actions}>
            <Button
              size='sm'
              className={styles.action}
              variant='secondary'
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBreakLiveElement}
              size='sm'
              className={styles.action}
              variant='primary'
            >
              Break
            </Button>
          </div>
        </div>
      </Property.Body>
    </Property.Dialog>
  )
}

export const EditLiveElementDialog = ({ widget, onCancel }) => {
  const id = _.get(widget, 'uid');
  const liveElement = _.get(widget, 'content.id');
  
  const workspace = useSelector((state => _.get(state, 'workspaces.workspace.item.id')));

  const handleEditLiveElement = () => {
    const url = `${process.env.REACT_APP_UI_URL}/app/${workspace}/liveElement/${liveElement}`;
    window.open(url, "_blank");

    handleClose();
  }

  const handleClose = () => {
    if (onCancel) {
      onCancel();
    }
  }

  return (
    <>
      <Property.Dialog className={styles.styleDialog}
        width={233}
        target={`#${id}`}
        title={'Edit live element:'}
        onCancel={handleClose}
      >
        <Property.Body>
          <div className={styles.container}>
            <p>Changes that you make to this live element will reflect across the storyboard.</p>
            <p>Are you sure you want to edit?</p>
            <div className={styles.actions}>
              <Button
                size='sm'
                className={styles.action}
                variant='secondary'
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                onClick={handleEditLiveElement}
                size='sm'
                className={styles.action}
                variant='primary'
              >
                Edit
              </Button>
            </div>
          </div>
        </Property.Body>
      </Property.Dialog>
    </>
  )
}

